import React, { useEffect, useMemo, useState } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  IconButton,
  TextField,
  Typography,
} from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  promptList: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    maxHeight: 260,
    overflowY: "auto",
    padding: theme.spacing(0.5, 1),
  },
  emptyPrompts: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    textAlign: "center",
  },
}));

const parseConfig = (jsonContent) => {
  if (!jsonContent) {
    return { apiKey: "", promptIds: [] };
  }

  try {
    const parsed = JSON.parse(jsonContent);
    return {
      apiKey: parsed.apiKey || "",
      promptIds: Array.isArray(parsed.promptIds)
        ? parsed.promptIds.map((id) => Number(id)).filter((id) => !Number.isNaN(id))
        : [],
    };
  } catch {
    return { apiKey: "", promptIds: [] };
  }
};

const WebSearchIntegrationModal = ({ open, onClose, integrationId }) => {
  const classes = useStyles();
  const [showApiKey, setShowApiKey] = useState(false);
  const [prompts, setPrompts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [storedApiKey, setStoredApiKey] = useState("");

  const initialState = useMemo(
    () => ({
      name: "",
      apiKey: "",
      promptIds: [],
    }),
    []
  );

  const [formValues, setFormValues] = useState(initialState);

  useEffect(() => {
    if (!open) return undefined;

    let active = true;

    const loadData = async () => {
      setLoading(true);
      try {
        const promptsRequest = api.get("/prompt");
        const integrationRequest = integrationId
          ? api.get(`/queueIntegration/${integrationId}`)
          : Promise.resolve(null);

        const [promptsResponse, integrationResponse] = await Promise.all([
          promptsRequest,
          integrationRequest,
        ]);

        if (!active) return;

        setPrompts(promptsResponse.data?.prompts || []);

        if (integrationResponse?.data) {
          const integration = integrationResponse.data;
          const config = parseConfig(integration.jsonContent);
          setStoredApiKey(config.apiKey || "");
          setFormValues({
            name: integration.name || "",
            apiKey: "",
            promptIds: config.promptIds,
          });
        } else {
          setStoredApiKey("");
          setFormValues(initialState);
        }
      } catch (err) {
        toastError(err);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      active = false;
    };
  }, [open, integrationId, initialState]);

  const handleClose = () => {
    setFormValues(initialState);
    setStoredApiKey("");
    onClose();
  };

  const schema = Yup.object().shape({
    name: Yup.string().min(2).max(100).required(i18n.t("common.required")),
    apiKey: Yup.string().when("$isEdit", {
      is: true,
      then: Yup.string().notRequired(),
      otherwise: Yup.string().required(
        i18n.t("webSearchIntegrationModal.form.apiKeyRequired")
      ),
    }),
    promptIds: Yup.array().of(Yup.number()),
  });

  const handleSave = async (values, { setSubmitting }) => {
    try {
      const apiKey = values.apiKey?.trim() || storedApiKey;
      if (!apiKey) {
        toast.error(i18n.t("webSearchIntegrationModal.form.apiKeyRequired"));
        setSubmitting(false);
        return;
      }

      const payload = {
        type: "tavily",
        name: values.name,
        projectName: values.name,
        language: "es",
        jsonContent: JSON.stringify({
          apiKey,
          promptIds: values.promptIds,
        }),
      };

      if (integrationId) {
        await api.put(`/queueIntegration/${integrationId}`, payload);
        toast.success(i18n.t("webSearchIntegrationModal.success.edit"));
      } else {
        await api.post("/queueIntegration", payload);
        toast.success(i18n.t("webSearchIntegrationModal.success.add"));
      }

      handleClose();
    } catch (err) {
      toastError(err);
      setSubmitting(false);
    }
  };

  const togglePromptId = (promptId, currentIds, setFieldValue) => {
    const normalizedId = Number(promptId);
    const nextIds = currentIds.includes(normalizedId)
      ? currentIds.filter((id) => id !== normalizedId)
      : [...currentIds, normalizedId];
    setFieldValue("promptIds", nextIds);
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>
        {integrationId
          ? i18n.t("webSearchIntegrationModal.title.edit")
          : i18n.t("webSearchIntegrationModal.title.add")}
      </DialogTitle>

      {loading ? (
        <DialogContent dividers>
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress size={32} />
          </Box>
        </DialogContent>
      ) : (
        <Formik
          initialValues={formValues}
          enableReinitialize
          validationSchema={schema}
          validationContext={{ isEdit: Boolean(integrationId) }}
          onSubmit={handleSave}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue }) => (
            <Form>
              <DialogContent dividers>
                <Field
                  as={TextField}
                  label={i18n.t("webSearchIntegrationModal.form.name")}
                  name="name"
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  required
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                />

                <Field
                  as={TextField}
                  label={i18n.t("webSearchIntegrationModal.form.apiKey")}
                  name="apiKey"
                  type={showApiKey ? "text" : "password"}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  error={touched.apiKey && Boolean(errors.apiKey)}
                  helperText={
                    (touched.apiKey && errors.apiKey) ||
                    (integrationId && storedApiKey
                      ? i18n.t("webSearchIntegrationModal.form.apiKeyEditHint")
                      : i18n.t("webSearchIntegrationModal.form.apiKeyHint"))
                  }
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowApiKey((prev) => !prev)}>
                          {showApiKey ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Box mt={2} mb={1}>
                  <Typography variant="subtitle2">
                    {i18n.t("webSearchIntegrationModal.form.promptsTitle")}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {i18n.t("webSearchIntegrationModal.form.promptsHint")}
                  </Typography>
                </Box>

                <Box className={classes.promptList}>
                  {!prompts.length ? (
                    <Typography className={classes.emptyPrompts}>
                      {i18n.t("webSearchIntegrationModal.form.noPrompts")}
                    </Typography>
                  ) : (
                    prompts.map((prompt) => (
                      <FormControlLabel
                        key={prompt.id}
                        control={
                          <Checkbox
                            color="primary"
                            checked={values.promptIds.includes(Number(prompt.id))}
                            onChange={() =>
                              togglePromptId(prompt.id, values.promptIds, setFieldValue)
                            }
                          />
                        }
                        label={`${prompt.name} (${prompt.model || "Open AI"})`}
                      />
                    ))
                  )}
                </Box>
              </DialogContent>

              <DialogActions>
                <Button onClick={handleClose} color="secondary" variant="outlined" disabled={isSubmitting}>
                  {i18n.t("webSearchIntegrationModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  disabled={isSubmitting}
                  className={classes.btnWrapper}
                >
                  {integrationId
                    ? i18n.t("webSearchIntegrationModal.buttons.save")
                    : i18n.t("webSearchIntegrationModal.buttons.add")}
                  {isSubmitting && (
                    <CircularProgress size={24} className={classes.buttonProgress} />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      )}
    </Dialog>
  );
};

export default WebSearchIntegrationModal;
